// The data-source pills drawn next to agent steps. They are derived from the
// agent nodes on every render and never enter the editor's node/edge state:
// toggling a source only writes the agent node's `data_sources`, so saving,
// dirty tracking, layout and deletion need no special cases for them.

import { VAYKEN_DATA_SOURCE, type DataSourceOption } from "../../../api/client.ts";
import { DS_GAP, DS_HEIGHT, DS_WIDTH, DS_X_GAP, NODE_HEIGHT } from "./layout.ts";
import type { DataSourceEdge, DataSourceNode, DataSourceNodeData, StepNode } from "./types.ts";
import { formatMoney } from "../../../utils/format.ts";

export const DS_ID_PREFIX = "ds:";
export const DS_EDGE_PREFIX = "ds-edge:";

export function dataSourceNodeId(agentNodeId: string, key: string): string {
  return `${DS_ID_PREFIX}${agentNodeId}:${key}`;
}

/** Whether a node/edge change from xyflow is about a derived element (to drop). */
export function isDataSourceChangeId(id: string): boolean {
  return id.startsWith(DS_ID_PREFIX) || id.startsWith(DS_EDGE_PREFIX);
}

// Drawn before the catalog lands so every agent has its Vayken pill from the start.
export const VAYKEN_FALLBACK: DataSourceOption = {
  key: VAYKEN_DATA_SOURCE,
  label: "Vayken",
  description: "The company database and collections, through the agent's Vayken tools.",
  implicit: true,
  always_on: false,
  spends_money: false,
  step_fields: [],
  configured: true,
  unavailable_reason: null,
};

// A saved key the catalog doesn't list (removed source, or a newer backend).
export function unknownOption(key: string): DataSourceOption {
  return {
    key,
    label: key,
    description: "This data source is not known to the server.",
    implicit: false,
    always_on: false,
    spends_money: false,
    step_fields: [],
    configured: false,
    unavailable_reason: "Unknown data source",
  };
}

/**
 * On for this agent whatever the step says: Vayken, or an always-on source the
 * agent's definition lists. Never stored on the step, never removable there.
 */
export function lockedOn(option: DataSourceOption, allowed: string[]): boolean {
  return option.implicit || (option.always_on && allowed.includes(option.key));
}

export interface DataSourceSelection {
  agentNodeId: string;
  key: string;
}

/** `sel` unless its agent node is gone or no longer has the key. */
export function activeSelection(
  sel: DataSourceSelection | null,
  stepNodes: StepNode[],
  catalog: DataSourceOption[],
): DataSourceSelection | null {
  if (!sel) return null;
  const agent = stepNodes.find((n) => n.id === sel.agentNodeId)?.data;
  if (agent?.kind !== "agent") return null;
  const locked = (catalog.length ? catalog : [VAYKEN_FALLBACK]).some(
    (o) => o.key === sel.key && lockedOn(o, agent.allowed_data_sources),
  );
  return locked || sel.key in agent.data_sources ? sel : null;
}

/** `$25` for 25 / "25.00", `$12.50` for 12.5; null when not a positive amount. */
export function formatSpendCap(value: unknown): string | null {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  if (!Number.isFinite(n) || n <= 0) return null;
  return formatMoney(n, "USD", { decimals: Number.isInteger(n) ? 0 : 2 });
}

/** The required money field of a source, if it has one. */
export function spendCapField(option: Pick<DataSourceOption, "step_fields">) {
  return option.step_fields.find((f) => f.required && f.type === "number");
}

/** The pill's one-liner: `Vayken · 12 tools`, `Clay · ≤ $25/run`, `Clay · no cap set`. */
export function summarizeDataSource(data: DataSourceNodeData): { text: string; error: boolean } {
  if (data.source_key === VAYKEN_DATA_SOURCE) {
    return { text: `${data.label} · ${data.vayken_tool_count} tools`, error: false };
  }
  const cap = spendCapField({ step_fields: data.fields });
  if (!cap) return { text: data.label, error: false };
  const formatted = formatSpendCap(data.config[cap.name]);
  return formatted
    ? { text: `${data.label} · ≤ ${formatted}/run`, error: false }
    : { text: `${data.label} · no cap set`, error: true };
}

/**
 * The pills and their edges for every agent node: the sources it always has
 * first (Vayken, then the always-on ones its definition lists), then the
 * opted-in ones in catalog order, then opted-in keys the catalog
 * doesn't know (so saved config is never hidden). Stacked left of the agent,
 * vertically centred on its card.
 */
export function dataSourceNodesFor(
  stepNodes: StepNode[],
  catalog: DataSourceOption[],
  selected: DataSourceSelection | null,
): { nodes: DataSourceNode[]; edges: DataSourceEdge[] } {
  const options = catalog.length ? catalog : [VAYKEN_FALLBACK];
  const nodes: DataSourceNode[] = [];
  const edges: DataSourceEdge[] = [];

  for (const agent of stepNodes) {
    const d = agent.data;
    if (d.kind !== "agent") continue;
    const opted = Object.keys(d.data_sources);
    const shown: { option: DataSourceOption; config: Record<string, unknown> }[] = [];
    const locked = (o: DataSourceOption) => lockedOn(o, d.allowed_data_sources);
    for (const o of options) if (o.implicit) shown.push({ option: o, config: {} });
    for (const o of options) if (!o.implicit && locked(o)) shown.push({ option: o, config: {} });
    for (const o of options) {
      // An always-on key left in a step's config (from before) is ignored by the server.
      if (!o.implicit && !o.always_on && opted.includes(o.key)) {
        shown.push({ option: o, config: d.data_sources[o.key] });
      }
    }
    for (const key of opted) {
      if (!options.some((o) => o.key === key)) {
        shown.push({ option: unknownOption(key), config: d.data_sources[key] });
      }
    }

    const stack = shown.length * DS_HEIGHT + (shown.length - 1) * DS_GAP;
    const x = agent.position.x - DS_X_GAP - DS_WIDTH;
    const top = agent.position.y + NODE_HEIGHT / 2 - stack / 2;
    shown.forEach(({ option, config }, i) => {
      const id = dataSourceNodeId(agent.id, option.key);
      const configured = option.implicit || option.configured;
      nodes.push({
        id,
        type: "data_source",
        position: { x, y: top + i * (DS_HEIGHT + DS_GAP) },
        // Both: `measured` so edges and fitView have bounds before the DOM is measured.
        width: DS_WIDTH,
        height: DS_HEIGHT,
        measured: { width: DS_WIDTH, height: DS_HEIGHT },
        selectable: false,
        draggable: false,
        connectable: false,
        deletable: false,
        focusable: false,
        selected: selected?.agentNodeId === agent.id && selected.key === option.key,
        data: {
          kind: "data_source",
          source_key: option.key,
          agent_node_id: agent.id,
          label: option.label,
          description: option.description,
          implicit: option.implicit,
          always_on: option.always_on,
          configured,
          spends_money: option.spends_money,
          fields: option.step_fields,
          config,
          vayken_tool_count: d.vayken_tool_count,
        },
      });
      edges.push({
        id: `${DS_EDGE_PREFIX}${id}`,
        source: id,
        sourceHandle: "out",
        target: agent.id,
        targetHandle: "data",
        type: "data_source",
        data: { configured },
        selectable: false,
        deletable: false,
        focusable: false,
      });
    });
  }

  return { nodes, edges };
}
